import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import client from '../../../lib/feathers';
import { setListing } from '../../../lib/listingsSlice';

/**
 * Alternate names a listing should also be findable by.
 *
 * Unlike libation aliases — which accumulate automatically when a duplicate is
 * merged in — there is no admin/listing-merge, so nothing fills these on its
 * own. They are typed here.
 *
 * What they are for: name lookups fail in both directions. "Two Lads" missed
 * "2 Lads Winery" entirely, and the Chamber of Commerce lists "Jester Cellars
 * LLC dba TruthTeller Winery" — a trade name no normaliser derives. The shared
 * normaliser handles number words and punctuation; only an alias handles a
 * genuinely different trading name.
 *
 * The server refuses an alias that collides with any other live listing's name
 * or alias, so a listing cannot claim a competitor's search term. That error is
 * surfaced verbatim below rather than flattened into "something went wrong" —
 * it names the conflicting listing, which is the whole point.
 */

const SOURCE_LABEL = {
  manual: 'added by hand',
  merge: 'from a merge',
  import: 'from an import',
  pipeline: 'from the pipeline',
};

export default function AliasesPanel() {
  const dispatch = useDispatch();
  const listing = useSelector((s) => s.listings.selected);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const aliases = Array.isArray(listing?.aliases) ? listing.aliases : [];

  const save = async (next) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await client.service('admin/listing').patch(listing.id, { aliases: next });
      dispatch(setListing(updated));
      setDraft('');
    } catch (err) {
      // The server's message names the conflicting listing — keep it.
      setError(err?.message || 'Could not save aliases');
    } finally {
      setBusy(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    const alias = draft.trim();
    if (!alias) return;
    await save([...aliases, { alias }]);
  };

  const remove = async (idx) => {
    await save(aliases.filter((_, i) => i !== idx));
  };

  return (
    <div className="bg-white shadow overflow-hidden px-4 py-5 sm:rounded-lg sm:p-6">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Also known as</h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Other names this winery should turn up under — a trade name, a former
          name, or a spelling people actually use. An alias cannot be a name
          another listing already uses.
        </p>
      </div>

      <div className="px-4 sm:px-6">
        {aliases.length === 0 ? (
          <p className="text-sm italic text-gray-400 mb-4">No alternate names yet.</p>
        ) : (
          <ul className="text-sm divide-y divide-gray-100 border border-gray-200 rounded-md mb-4">
            {aliases.map((a, idx) => (
              <li
                key={`${a.alias}-${idx}`}
                className="px-3 py-2 flex justify-between items-baseline gap-3"
              >
                <span className="font-mono text-gray-800 break-all">{a.alias}</span>
                <span className="flex items-baseline gap-3 whitespace-nowrap">
                  <span className="text-xs text-gray-500">
                    {SOURCE_LABEL[a.source] || a.source || 'added by hand'}
                    {a.addedAt ? ` · ${new Date(a.addedAt).toISOString().slice(0, 10)}` : ''}
                  </span>
                  <button
                    type="button"
                    onClick={() => remove(idx)}
                    disabled={busy}
                    className="text-xs text-red-700 hover:text-red-900 disabled:opacity-50"
                  >
                    Remove
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}

        <form onSubmit={add} className="flex gap-2 items-start">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. 2 Lads Winery"
            disabled={busy}
            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
          />
          <button
            type="submit"
            disabled={busy || !draft.trim()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-red-900 disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Add'}
          </button>
        </form>

        {error && (
          <div className="mt-3 rounded-md bg-red-50 p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
